import { Link } from "react-router-dom";
import type { BranchDetail } from "@/types";
import { formatSAR } from "@/utils/format";

interface BranchCardProps {
  branch: BranchDetail;
  onToggleOpen: (id: string, value: boolean) => void;
  onToggleActive: (id: string, value: boolean) => void;
  onEdit: (branch: BranchDetail) => void;
}

export default function BranchCard({
  branch,
  onToggleOpen,
  onToggleActive,
  onEdit,
}: BranchCardProps) {
  return (
    <article
      className={`yc-fade-up yc-card-hover rounded-card border shadow-card p-5 flex flex-col ${
        branch.isActive
          ? "bg-white border-background-200"
          : "bg-background-50 border-background-200"
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-3 min-w-0">
          <span
            className={`w-11 h-11 flex items-center justify-center rounded-full text-lg shrink-0 ${
              branch.isActive
                ? "bg-primary-100 text-primary-700"
                : "bg-background-200 text-foreground-400"
            }`}
          >
            <i className="ri-store-3-line"></i>
          </span>
          <div className="min-w-0">
            <h3 className="text-base font-bold text-foreground-950 truncate">
              {branch.name}
            </h3>
            <p className="text-xs text-foreground-500">{branch.city}</p>
          </div>
        </div>
        <span
          className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold whitespace-nowrap ${
            !branch.isActive
              ? "bg-red-100 text-red-600"
              : branch.isOpen
                ? "bg-primary-100 text-primary-800"
                : "bg-secondary-200 text-secondary-800"
          }`}
        >
          {!branch.isActive ? "معطّل" : branch.isOpen ? "مفتوح" : "مغلق"}
        </span>
      </div>

      <dl className="mt-4 space-y-2 text-sm">
        <div className="flex items-center gap-2 text-foreground-600">
          <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
            <i className="ri-map-pin-line"></i>
          </span>
          <span className="truncate">{branch.address}</span>
        </div>
        <div className="flex items-center gap-2 text-foreground-600">
          <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
            <i className="ri-time-line"></i>
          </span>
          <span>{branch.hours}</span>
        </div>
        <div className="flex items-center gap-2 text-foreground-600">
          <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
            <i className="ri-user-star-line"></i>
          </span>
          <span className={branch.manager ? "" : "text-foreground-400"}>
            {branch.manager
              ? `مدير الفرع: ${branch.manager}`
              : "لا يوجد مدير فرع مضاف"}
          </span>
        </div>
      </dl>

      <div className="mt-4 grid grid-cols-3 gap-2">
        <div className="rounded-btn bg-background-50 px-3 py-2 text-center">
          <p className="text-[11px] text-foreground-500">موظفون</p>
          <p className="text-sm font-bold text-foreground-950">
            {branch.staffCount}
          </p>
        </div>
        <div className="rounded-btn bg-background-50 px-3 py-2 text-center">
          <p className="text-[11px] text-foreground-500">طلبات اليوم</p>
          <p className="text-sm font-bold text-foreground-950">
            {branch.ordersToday}
          </p>
        </div>
        <div className="rounded-btn bg-accent-50 px-3 py-2 text-center">
          <p className="text-[11px] text-accent-800">مبيعات اليوم</p>
          <p className="text-sm font-bold text-accent-900">
            {formatSAR(branch.salesToday)}
          </p>
        </div>
      </div>

      <div className="mt-4 pt-3 border-t border-background-100 flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <button
            type="button"
            role="switch"
            aria-checked={branch.isOpen}
            disabled={!branch.isActive}
            onClick={() => onToggleOpen(branch.id, !branch.isOpen)}
            className={`flex-1 h-10 rounded-btn text-sm font-semibold transition-colors duration-200 whitespace-nowrap ${
              !branch.isActive
                ? "bg-background-100 text-foreground-300 cursor-not-allowed"
                : branch.isOpen
                  ? "bg-secondary-100 text-secondary-900 hover:bg-secondary-200 cursor-pointer"
                  : "bg-primary-500 text-white hover:bg-primary-600 cursor-pointer"
            }`}
          >
            {branch.isOpen ? "إغلاق الفرع مؤقتًا" : "فتح الفرع الآن"}
          </button>
          <button
            type="button"
            onClick={() => onToggleActive(branch.id, !branch.isActive)}
            className={`w-10 h-10 flex items-center justify-center rounded-btn transition-colors duration-200 cursor-pointer ${
              branch.isActive
                ? "bg-background-100 text-foreground-500 hover:bg-red-100 hover:text-red-600"
                : "bg-primary-100 text-primary-700 hover:bg-primary-200"
            }`}
            aria-label={branch.isActive ? "تعطيل الفرع" : "تفعيل الفرع"}
            title={branch.isActive ? "تعطيل الفرع" : "تفعيل الفرع"}
          >
            <i
              className={
                branch.isActive ? "ri-pause-circle-line" : "ri-play-circle-line"
              }
            ></i>
          </button>
          <button
            type="button"
            onClick={() => onEdit(branch)}
            className="w-10 h-10 flex items-center justify-center rounded-btn bg-background-100 text-foreground-500 hover:bg-primary-100 hover:text-primary-800 transition-colors duration-200 cursor-pointer"
            aria-label="تعديل الفرع"
            title="تعديل الفرع"
          >
            <i className="ri-edit-line"></i>
          </button>
        </div>

        <div className="flex items-center gap-2">
          <Link
            to={`/branches/${branch.id}`}
            className="flex-1 inline-flex items-center justify-center gap-1.5 h-10 rounded-btn bg-background-100 text-xs font-semibold text-foreground-600 hover:bg-background-200 transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-file-list-3-line"></i>
            تفاصيل الفرع
          </Link>
          <Link
            to={`/employees?branch=${branch.id}`}
            className="flex-1 inline-flex items-center justify-center gap-1.5 h-10 rounded-btn bg-accent-100 text-xs font-semibold text-accent-900 hover:bg-accent-200 transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-team-line"></i>
            إدارة موظفي الفرع
          </Link>
        </div>
      </div>
    </article>
  );
}